import React, { useState, useEffect, useCallback, useRef } from 'react';
import { projectsApi, tasksApi, filesApi } from '../lib/api';
import DashboardLayout from '../components/DashLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Plus,
  CalendarIcon,
  Trash2,
  FolderKanban,
  Loader2,
  Check,
  FileText,
  Paperclip,
  Download,
  Upload,
  X,
} from 'lucide-react';
import { format, parseISO, differenceInDays, addDays, startOfMonth, endOfMonth, eachDayOfInterval, eachWeekOfInterval, startOfWeek, isToday, isSameMonth, getWeek } from 'date-fns';
import { cn } from '../lib/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TASK_COLORS = [
  { value: '#3B82F6', label: 'Blue' },
  { value: '#10B981', label: 'Green' },
  { value: '#F59E0B', label: 'Amber' },
  { value: '#8B5CF6', label: 'Purple' },
  { value: '#EC4899', label: 'Pink' },
  { value: '#06B6D4', label: 'Cyan' },
];

const GanttView = () => {
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [zoomLevel, setZoomLevel] = useState('week');
  const [showTaskDialog, setShowTaskDialog] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [taskForm, setTaskForm] = useState({
    name: '',
    start_date: new Date().toISOString().split('T')[0],
    end_date: addDays(new Date(), 7).toISOString().split('T')[0],
    color: '#3B82F6',
    progress: 0,
    notes: '',
  });
  
  const [taskFiles, setTaskFiles] = useState([]);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [savingTask, setSavingTask] = useState(false);
  const [activeTab, setActiveTab] = useState('details');
  const fileInputRef = useRef(null);
  const colorInputRef = useRef(null);
  const timelineRef = useRef(null);
  const savingRef = useRef(false);
  
  const [dragging, setDragging] = useState(null);
  const [resizing, setResizing] = useState(null);
  const [timelineRange, setTimelineRange] = useState({ start: null, end: null });
  
  // Timeline drag scroll state
  const [isScrolling, setIsScrolling] = useState(false);
  const [scrollStart, setScrollStart] = useState({ x: 0, scrollLeft: 0 });

  // Date picker state
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [jumpToDate, setJumpToDate] = useState(new Date());

  // Orientation change key to force re-render
  const [orientationKey, setOrientationKey] = useState(0);
  const lastOrientationRef = useRef(window.innerWidth > window.innerHeight ? 'landscape' : 'portrait');

  // Handle orientation changes
  useEffect(() => {
    const handleOrientationChange = () => {
      const currentOrientation = window.innerWidth > window.innerHeight ? 'landscape' : 'portrait';

      // Only trigger if orientation actually changed
      if (currentOrientation !== lastOrientationRef.current) {
        lastOrientationRef.current = currentOrientation;
        // Close any open popovers to reset state
        setShowDatePicker(false);
        // Force re-render of dialog by updating key
        setOrientationKey(prev => prev + 1);
      }
    };

    // Listen for orientation change
    window.addEventListener('orientationchange', handleOrientationChange);
    // Also listen for resize as backup (some browsers don't fire orientationchange)
    window.addEventListener('resize', handleOrientationChange);

    return () => {
      window.removeEventListener('orientationchange', handleOrientationChange);
      window.removeEventListener('resize', handleOrientationChange);
    };
  }, []);

  // Infinite scroll extension buffer
  const EXTEND_DAYS = 60;

  // Zoom level configurations
  const CELL_WIDTH = {
    day: 50,
    week: 40,
    month: 25,
  };

  useEffect(() => {
    loadProjects();
  }, []);

  useEffect(() => {
    if (selectedProject) {
      loadTasks(selectedProject.id);
    }
  }, [selectedProject]);

  // WebSocket connection for real-time updates
  useEffect(() => {
    if (!selectedProject) return;

    // Convert HTTP URL to WebSocket URL
    const wsProtocol = BACKEND_URL?.startsWith('https') ? 'wss' : 'ws';
    const wsHost = BACKEND_URL?.replace(/^https?:\/\//, '') || 'localhost:8000';
    const wsUrl = `${wsProtocol}://${wsHost}/ws/${selectedProject.id}`;

    let ws = null;
    let reconnectTimeout = null;
    let pingInterval = null;

    const connect = () => {
      ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        console.log('WebSocket connected');
        // Send ping every 30 seconds to keep connection alive
        pingInterval = setInterval(() => {
          if (ws?.readyState === WebSocket.OPEN) {
            ws.send('ping');
          }
        }, 30000);
      };

      ws.onmessage = (event) => {
        if (event.data === 'pong') return; // Ignore pong responses

        try {
          const message = JSON.parse(event.data);

          if (message.type === 'task_created') {
            setTasks(prev => [...prev, message.task]);
          } else if (message.type === 'task_updated') {
            setTasks(prev => prev.map(t => t.id === message.task.id ? message.task : t));
          } else if (message.type === 'task_deleted') {
            setTasks(prev => prev.filter(t => t.id !== message.task_id));
          }
        } catch (e) {
          console.error('Failed to parse WebSocket message:', e);
        }
      };

      ws.onclose = () => {
        console.log('WebSocket disconnected');
        clearInterval(pingInterval);
        // Reconnect after 3 seconds
        reconnectTimeout = setTimeout(connect, 3000);
      };

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
      };
    };

    connect();

    return () => {
      clearTimeout(reconnectTimeout);
      clearInterval(pingInterval);
      if (ws) {
        ws.close();
      }
    };
  }, [selectedProject]);

  // Initialize timeline range centered on today
  useEffect(() => {
    const now = new Date();
    // Always include a range around today, plus task dates if any
    let rangeStart = addDays(now, -30);
    let rangeEnd = addDays(now, 90);

    if (tasks.length > 0) {
      const startDates = tasks.map((t) => parseISO(t.start_date));
      const endDates = tasks.map((t) => parseISO(t.end_date));
      const minTaskDate = new Date(Math.min(...startDates));
      const maxTaskDate = new Date(Math.max(...endDates));

      // Extend range to include all tasks
      if (minTaskDate < rangeStart) rangeStart = addDays(minTaskDate, -14);
      if (maxTaskDate > rangeEnd) rangeEnd = addDays(maxTaskDate, 30);
    }

    setTimelineRange({ start: rangeStart, end: rangeEnd });
  }, [tasks]);

  // Scroll to today on initial load
  useEffect(() => {
    if (timelineRef.current && timelineRange.start) {
      const now = new Date();
      const daysSinceStart = differenceInDays(now, timelineRange.start);
      const cellWidth = CELL_WIDTH[zoomLevel];
      const scrollPosition = Math.max(0, (daysSinceStart * cellWidth) - (timelineRef.current.clientWidth / 3));
      timelineRef.current.scrollLeft = scrollPosition;
    }
  }, [timelineRange.start, zoomLevel]);

  const loadProjects = async () => {
    try {
      const response = await projectsApi.list();
      setProjects(response.data);
      if (response.data.length > 0 && !selectedProject) {
        setSelectedProject(response.data[0]);
      }
    } catch (error) {
      console.error('Failed to load projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTasks = async (projectId) => {
    try {
      const response = await tasksApi.list(projectId);
      setTasks(response.data);
    } catch (error) {
      console.error('Failed to load tasks:', error);
    }
  };

  const loadTaskFiles = async (taskId) => {
    try {
      const response = await filesApi.listAttachments('task', taskId);
      setTaskFiles(response.data || []);
    } catch (error) {
      console.error('Failed to load task files:', error);
      setTaskFiles([]);
    }
  };

  const handleCreateTask = async () => {
    // Double-check with ref to prevent race conditions
    if (!selectedProject || !taskForm.name.trim() || savingTask || savingRef.current) return;

    savingRef.current = true;
    setSavingTask(true);
    try {
      if (editingTask) {
        await tasksApi.update(editingTask.id, taskForm);
      } else {
        await tasksApi.create(selectedProject.id, taskForm);
      }
      await loadTasks(selectedProject.id);
      setShowTaskDialog(false);
      setEditingTask(null);
      setTaskForm({
        name: '',
        start_date: new Date().toISOString().split('T')[0],
        end_date: addDays(new Date(), 7).toISOString().split('T')[0],
        color: '#3B82F6',
        progress: 0,
        notes: '',
      });
      setTaskFiles([]);
      setActiveTab('details');
    } catch (error) {
      console.error('Failed to save task:', error);
    } finally {
      setSavingTask(false);
      savingRef.current = false;
    }
  };

  const handleDeleteTask = async (taskId) => {
    try {
      await tasksApi.delete(taskId);
      await loadTasks(selectedProject.id);
    } catch (error) {
      console.error('Failed to delete task:', error);
    }
  };

  const handleEditTask = async (task) => {
    setEditingTask(task);
    setTaskForm({
      name: task.name,
      start_date: task.start_date,
      end_date: task.end_date,
      color: task.color,
      progress: task.progress,
      notes: task.notes || '',
    });
    setActiveTab('details');
    await loadTaskFiles(task.id);
    setShowTaskDialog(true);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !editingTask) return;

    setUploadingFile(true);
    try {
      await filesApi.uploadAttachment('task', editingTask.id, file);
      await loadTaskFiles(editingTask.id);
    } catch (error) {
      console.error('Failed to upload file:', error);
      alert('Failed to upload file. Please try again.');
    } finally {
      setUploadingFile(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDeleteFile = async (fileId) => {
    try {
      const token = localStorage.getItem('chronoflow_token');
      await fetch(`${BACKEND_URL}/api/files/${fileId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      if (editingTask) {
        await loadTaskFiles(editingTask.id);
      }
    } catch (error) {
      console.error('Failed to delete file:', error);
    }
  };

  const handleDownloadFile = (fileId, filename) => {
    const token = localStorage.getItem('chronoflow_token');
    window.open(`${BACKEND_URL}/api/files/attachment/${fileId}?token=${token}`, '_blank');
  };

  const handleTaskDrag = useCallback(async (taskId, newStartDate, newEndDate) => {
    try {
      await tasksApi.update(taskId, {
        start_date: newStartDate,
        end_date: newEndDate,
      });
    } catch (error) {
      console.error('Failed to update task:', error);
    }
  }, []);

  const handleTaskResize = useCallback(async (taskId, newEndDate) => {
    try {
      await tasksApi.update(taskId, { end_date: newEndDate });
    } catch (error) {
      console.error('Failed to update task:', error);
    }
  }, []);

  // Generate timeline data based on zoom level
  const getTimelineData = useCallback(() => {
    if (!timelineRange.start || !timelineRange.end) return { headers: [], cells: [] };

    const days = eachDayOfInterval({ start: timelineRange.start, end: timelineRange.end });
    const cellWidth = CELL_WIDTH[zoomLevel];
    
    if (zoomLevel === 'day') {
      // Day view: Show individual days grouped by month
      const headers = [];
      const cells = [];
      let currentMonth = null;
      let monthStart = 0;
      let monthWidth = 0;

      days.forEach((day, index) => {
        const monthKey = format(day, 'MMM yyyy');
        if (monthKey !== currentMonth) {
          if (currentMonth) {
            headers.push({ label: currentMonth, width: monthWidth, start: monthStart });
          }
          currentMonth = monthKey;
          monthStart = index * cellWidth;
          monthWidth = 0;
        }
        monthWidth += cellWidth;
        cells.push({
          date: day,
          label: format(day, 'd'),
          subLabel: format(day, 'EEE'),
          width: cellWidth,
          isToday: isToday(day),
          isWeekend: [0, 6].includes(day.getDay()),
        });
      });
      if (currentMonth) {
        headers.push({ label: currentMonth, width: monthWidth, start: monthStart });
      }

      return { headers, cells };
    } else if (zoomLevel === 'week') {
      // Week view: Show weeks grouped by month
      const headers = [];
      const cells = [];
      let currentMonth = null;
      let monthStart = 0;
      let monthWidth = 0;

      days.forEach((day, index) => {
        const monthKey = format(day, 'MMM yyyy');
        if (monthKey !== currentMonth) {
          if (currentMonth) {
            headers.push({ label: currentMonth, width: monthWidth, start: monthStart });
          }
          currentMonth = monthKey;
          monthStart = index * cellWidth;
          monthWidth = 0;
        }
        monthWidth += cellWidth;
        cells.push({
          date: day,
          label: format(day, 'd'),
          subLabel: day.getDay() === 1 ? `W${getWeek(day)}` : '',
          width: cellWidth,
          isToday: isToday(day),
          isWeekend: [0, 6].includes(day.getDay()),
          isMonday: day.getDay() === 1,
        });
      });
      if (currentMonth) {
        headers.push({ label: currentMonth, width: monthWidth, start: monthStart });
      }

      return { headers, cells };
    } else {
      // Month view: Show days grouped by month
      const headers = [];
      const cells = [];
      let currentMonth = null;
      let monthStart = 0;
      let monthWidth = 0;

      days.forEach((day, index) => {
        const monthKey = format(day, 'MMM yyyy');
        if (monthKey !== currentMonth) {
          if (currentMonth) {
            headers.push({ label: currentMonth, width: monthWidth, start: monthStart });
          }
          currentMonth = monthKey;
          monthStart = index * cellWidth;
          monthWidth = 0;
        }
        monthWidth += cellWidth;
        cells.push({
          date: day,
          label: day.getDate() === 1 || day.getDate() === 15 ? format(day, 'd') : '',
          width: cellWidth,
          isToday: isToday(day),
          isWeekend: [0, 6].includes(day.getDay()),
          isFirstOfMonth: day.getDate() === 1,
        });
      });
      if (currentMonth) {
        headers.push({ label: currentMonth, width: monthWidth, start: monthStart });
      }

      return { headers, cells };
    }
  }, [zoomLevel, timelineRange]);

  const calculateTaskPosition = useCallback(
    (task) => {
      if (!timelineRange.start) return { left: 0, width: 0 };

      const cellWidth = CELL_WIDTH[zoomLevel];
      const taskStart = parseISO(task.start_date);
      const taskEnd = parseISO(task.end_date);
      
      const startOffset = differenceInDays(taskStart, timelineRange.start);
      const duration = differenceInDays(taskEnd, taskStart) + 1;
      
      return {
        left: startOffset * cellWidth,
        width: Math.max(duration * cellWidth, cellWidth),
      };
    },
    [timelineRange, zoomLevel]
  );

  const { headers, cells } = getTimelineData();
  const totalWidth = cells.reduce((sum, c) => sum + c.width, 0);

  // Extend timeline when user scrolls near edges
  const extendTimelineIfNeeded = useCallback(() => {
    if (!timelineRef.current || !timelineRange.start || !timelineRange.end) return;

    const { scrollLeft, scrollWidth, clientWidth } = timelineRef.current;
    const cellWidth = CELL_WIDTH[zoomLevel];
    const threshold = cellWidth * 10; // Extend when within 10 cells of edge

    // Extend to the right (future)
    if (scrollLeft + clientWidth >= scrollWidth - threshold) {
      setTimelineRange(prev => ({
        ...prev,
        end: addDays(prev.end, EXTEND_DAYS),
      }));
    }

    // Extend to the left (past)
    if (scrollLeft <= threshold) {
      const prevScrollWidth = scrollWidth;
      setTimelineRange(prev => {
        const newStart = addDays(prev.start, -EXTEND_DAYS);
        // After state update, we need to adjust scroll position
        setTimeout(() => {
          if (timelineRef.current) {
            const newScrollWidth = timelineRef.current.scrollWidth;
            const addedWidth = newScrollWidth - prevScrollWidth;
            timelineRef.current.scrollLeft = scrollLeft + addedWidth;
          }
        }, 0);
        return { ...prev, start: newStart };
      });
    }
  }, [timelineRange, zoomLevel]);

  // Timeline drag-to-scroll handlers
  const handleTimelineMouseDown = (e) => {
    // Only start scrolling if not clicking on a task bar
    if (e.target.closest('[data-task-bar]')) return;

    setIsScrolling(true);
    setScrollStart({
      x: e.clientX,
      scrollLeft: timelineRef.current?.scrollLeft || 0,
    });
  };

  const handleTimelineMouseMove = (e) => {
    if (!isScrolling || !timelineRef.current) return;

    const dx = e.clientX - scrollStart.x;
    timelineRef.current.scrollLeft = scrollStart.scrollLeft - dx;
    extendTimelineIfNeeded();
  };

  const handleTimelineMouseUp = () => {
    setIsScrolling(false);
  };

  // Handle horizontal wheel scroll
  const handleTimelineWheel = (e) => {
    if (!timelineRef.current) return;
    // Allow horizontal scroll with Shift+wheel or horizontal track-pad gesture
    if (e.shiftKey || Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
      e.preventDefault();
      timelineRef.current.scrollLeft += e.deltaX || e.deltaY;
      extendTimelineIfNeeded();
    }
  };

  // Scroll to a specific date
  const scrollToDate = useCallback((date) => {
    if (!timelineRef.current || !timelineRange.start) return;

    const targetDate = date instanceof Date ? date : parseISO(date);
    const cellWidth = CELL_WIDTH[zoomLevel];

    // Extend timeline if date is outside current range
    if (targetDate < timelineRange.start) {
      const daysToExtend = differenceInDays(timelineRange.start, targetDate) + 30;
      setTimelineRange(prev => ({ ...prev, start: addDays(prev.start, -daysToExtend) }));
    } else if (targetDate > timelineRange.end) {
      const daysToExtend = differenceInDays(targetDate, timelineRange.end) + 30;
      setTimelineRange(prev => ({ ...prev, end: addDays(prev.end, daysToExtend) }));
    }

    // Scroll to the date (center it in view)
    setTimeout(() => {
      if (timelineRef.current) {
        const daysSinceStart = differenceInDays(targetDate, timelineRange.start);
        const scrollPosition = Math.max(0, (daysSinceStart * cellWidth) - (timelineRef.current.clientWidth / 3));
        timelineRef.current.scrollTo({ left: scrollPosition, behavior: 'smooth' });
      }
    }, 50);

    setShowDatePicker(false);
  }, [timelineRange, zoomLevel]);

  // Scroll to today
  const scrollToToday = useCallback(() => {
    scrollToDate(new Date());
  }, [scrollToDate]);

  useEffect(() => {
    if (isScrolling) {
      window.addEventListener('mousemove', handleTimelineMouseMove);
      window.addEventListener('mouseup', handleTimelineMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', handleTimelineMouseMove);
      window.removeEventListener('mouseup', handleTimelineMouseUp);
    };
  }, [isScrolling, scrollStart]);

  // Mouse handlers for task drag/resize
  const handleMouseDown = (e, task, action) => {
    e.preventDefault();
    e.stopPropagation();
    
    const startX = e.clientX;
    
    if (action === 'drag') {
      setDragging({
        task,
        startX,
        originalStart: task.start_date,
        originalEnd: task.end_date,
      });
    } else if (action === 'resize') {
      setResizing({
        task,
        startX,
        originalEnd: task.end_date,
      });
    }
  };

  useEffect(() => {
    const cellWidth = CELL_WIDTH[zoomLevel];
    
    const handleMouseMove = (e) => {
      if (dragging) {
        const deltaX = e.clientX - dragging.startX;
        const daysDelta = Math.round(deltaX / cellWidth);
        
        if (daysDelta !== 0) {
          const newStart = addDays(parseISO(dragging.originalStart), daysDelta);
          const newEnd = addDays(parseISO(dragging.originalEnd), daysDelta);
          
          setTasks((prev) =>
            prev.map((t) =>
              t.id === dragging.task.id
                ? { ...t, start_date: format(newStart, 'yyyy-MM-dd'), end_date: format(newEnd, 'yyyy-MM-dd') }
                : t
            )
          );
        }
      }
      
      if (resizing) {
        const deltaX = e.clientX - resizing.startX;
        const daysDelta = Math.round(deltaX / cellWidth);
        
        if (daysDelta !== 0) {
          const newEnd = addDays(parseISO(resizing.originalEnd), daysDelta);
          const task = tasks.find((t) => t.id === resizing.task.id);
          
          if (task && newEnd >= parseISO(task.start_date)) {
            setTasks((prev) =>
              prev.map((t) =>
                t.id === resizing.task.id ? { ...t, end_date: format(newEnd, 'yyyy-MM-dd') } : t
              )
            );
          }
        }
      }
    };

    const handleMouseUp = async () => {
      if (dragging) {
        const task = tasks.find((t) => t.id === dragging.task.id);
        if (task) {
          await handleTaskDrag(task.id, task.start_date, task.end_date);
        }
        setDragging(null);
      }
      
      if (resizing) {
        const task = tasks.find((t) => t.id === resizing.task.id);
        if (task) {
          await handleTaskResize(task.id, task.end_date);
        }
        setResizing(null);
      }
    };

    if (dragging || resizing) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [dragging, resizing, tasks, zoomLevel, handleTaskDrag, handleTaskResize]);

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-4 min-w-0 max-w-full" data-testid="gantt-view">
        {/* Toolbar - Fixed at top */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between sticky top-0 z-30 bg-[#050505] py-2 gap-3">
          <div className="flex flex-wrap items-center gap-2 sm:gap-4">
            <Select
              value={selectedProject?.id || ''}
              onValueChange={(id) => setSelectedProject(projects.find((p) => p.id === id))}
            >
              <SelectTrigger className="w-[160px] sm:w-[200px] bg-[#0A0A0B] border-[#27272A]" data-testid="project-selector">
                <SelectValue placeholder="Select a project" />
              </SelectTrigger>
              <SelectContent className="bg-[#121214] border-[#27272A]">
                {projects.map((project) => (
                  <SelectItem key={project.id} value={project.id}>
                    <div className="flex items-center gap-2">
                      <FolderKanban className="w-4 h-4 text-primary" />
                      {project.name}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div className="flex items-center gap-1 bg-[#0A0A0B] rounded-lg p-1 border border-[#27272A]">
              {['day', 'week', 'month'].map((level) => (
                <Button
                  key={level}
                  variant={zoomLevel === level ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setZoomLevel(level)}
                  className={cn(
                    'h-8 px-2 sm:px-3 capitalize text-xs sm:text-sm',
                    zoomLevel === level ? 'bg-primary' : 'hover:bg-white/5'
                  )}
                  data-testid={`zoom-${level}`}
                >
                  {level}
                </Button>
              ))}
            </div>

            <Button
              onClick={() => {
                setEditingTask(null);
                setTaskForm({
                  name: '',
                  start_date: new Date().toISOString().split('T')[0],
                  end_date: addDays(new Date(), 7).toISOString().split('T')[0],
                  color: '#3B82F6',
                  progress: 0,
                  notes: '',
                });
                setTaskFiles([]);
                setActiveTab('details');
                setShowTaskDialog(true);
              }}
              size="sm"
              className="h-8 bg-primary hover:bg-primary/90"
              disabled={!selectedProject}
              data-testid="add-task-btn"
            >
              <Plus className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Add Task</span>
            </Button>

            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={scrollToToday}
                className="h-8 border-[#27272A] hover:bg-white/5 text-xs sm:text-sm"
              >
                Today
              </Button>

              <Popover open={showDatePicker} onOpenChange={setShowDatePicker}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 border-[#27272A] hover:bg-white/5"
                  >
                    <CalendarIcon className="w-4 h-4 sm:mr-2" />
                    <span className="hidden sm:inline">Go to Date</span>
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-[#121214] border-[#27272A]" align="start">
                  <Calendar
                    mode="single"
                    selected={jumpToDate}
                    onSelect={(date) => {
                      if (date) {
                        setJumpToDate(date);
                        scrollToDate(date);
                      }
                    }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>
        </div>

        {/* Gantt Chart */}
        {projects.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-[60vh] text-center">
            <FolderKanban className="w-16 h-16 text-muted-foreground mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">No Projects Yet</h3>
            <p className="text-muted-foreground">Go to Admin Panel to create a project first.</p>
          </div>
        ) : !selectedProject ? (
          <div className="flex flex-col items-center justify-center h-[60vh] text-center">
            <FolderKanban className="w-16 h-16 text-muted-foreground mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">Select a Project</h3>
            <p className="text-muted-foreground">Choose a project from the dropdown above.</p>
          </div>
        ) : (
          <div className="bg-[#0A0A0B] rounded-xl border border-[#27272A] min-w-0 overflow-hidden">
            <div className="flex min-w-0">
              {/* Task List - Fixed left panel */}
              <div className="w-[120px] sm:w-[180px] md:w-[250px] min-w-[120px] sm:min-w-[180px] md:min-w-[250px] flex-shrink-0 border-r border-[#27272A]">
                <div className="h-[72px] border-b border-[#27272A] flex items-center px-2 sm:px-4 bg-[#0A0A0B]">
                  <span className="font-semibold text-white text-sm sm:text-base">Tasks</span>
                  <span className="ml-1 sm:ml-2 text-xs sm:text-sm text-muted-foreground">({tasks.length})</span>
                </div>

                <div className="overflow-y-auto" style={{ maxHeight: 'calc(100vh - 300px)' }}>
                  {tasks.length === 0 ? (
                    <div className="p-4 sm:p-6 text-center text-muted-foreground">
                      <p className="text-sm">No tasks yet</p>
                      <p className="text-xs sm:text-sm">Click "Add Task" to create one</p>
                    </div>
                  ) : (
                    tasks.map((task) => (
                      <div
                        key={task.id}
                        className="h-10 border-b border-[#1a1a1a] flex items-center px-2 sm:px-4 hover:bg-white/5 active:bg-white/10 group cursor-pointer"
                        onClick={() => handleEditTask(task)}
                        data-testid={`task-row-${task.id}`}
                      >
                        <div
                          className="w-2 h-2 sm:w-3 sm:h-3 rounded-full mr-2 sm:mr-3 flex-shrink-0"
                          style={{ backgroundColor: task.color }}
                        />
                        <span className="text-xs sm:text-sm text-white truncate flex-1">
                          {task.name}
                        </span>
                        {task.notes && <FileText className="w-3 h-3 text-muted-foreground mr-1 hidden sm:block" />}
                        <Button
                          variant="ghost"
                          size="icon"
                          className="w-6 h-6 flex-shrink-0 text-muted-foreground hover:text-destructive active:text-destructive sm:opacity-0 sm:group-hover:opacity-100"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteTask(task.id);
                          }}
                          data-testid={`delete-task-${task.id}`}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Timeline - Scrollable right panel */}
              <div
                ref={timelineRef}
                className={cn(
                  "flex-1 overflow-x-auto overflow-y-hidden select-none min-w-0 touch-pan-x",
                  isScrolling ? "cursor-grabbing" : "cursor-grab"
                )}
                style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}
                onMouseDown={handleTimelineMouseDown}
                onScroll={extendTimelineIfNeeded}
                onWheel={handleTimelineWheel}
              >
                <div style={{ width: totalWidth, minWidth: totalWidth }}>
                  {/* Header Row 1 - Months */}
                  <div className="h-9 border-b border-[#27272A] flex bg-[#0A0A0B] sticky top-0 z-10">
                    {headers.map((header, index) => (
                      <div
                        key={index}
                        className="flex-shrink-0 border-r border-[#27272A] flex items-center justify-center text-sm font-medium text-white"
                        style={{ width: header.width }}
                      >
                        {header.label}
                      </div>
                    ))}
                  </div>

                  {/* Header Row 2 - Days/Weeks */}
                  <div className="h-8 border-b border-[#27272A] flex bg-[#0A0A0B] sticky top-9 z-10">
                    {cells.map((cell, index) => (
                      <div
                        key={index}
                        className={cn(
                          'flex-shrink-0 flex flex-col items-center justify-center text-xs',
                          cell.isToday && 'bg-primary/20',
                          cell.isWeekend && !cell.isToday && 'bg-white/5',
                          cell.isMonday && 'border-l border-[#27272A]',
                          cell.isFirstOfMonth && 'border-l border-[#27272A]'
                        )}
                        style={{ width: cell.width }}
                      >
                        <span className={cn('text-muted-foreground', cell.isToday && 'text-primary font-bold')}>
                          {cell.label}
                        </span>
                        {cell.subLabel && (
                          <span className="text-[10px] text-muted-foreground">{cell.subLabel}</span>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Task Rows */}
                  <div className="relative" style={{ height: Math.max(tasks.length * 40, 200) }}>
                    {/* Grid background */}
                    <div className="absolute inset-0 flex pointer-events-none">
                      {cells.map((cell, index) => (
                        <div
                          key={index}
                          className={cn(
                            'flex-shrink-0 border-r h-full',
                            cell.isToday ? 'bg-primary/10 border-primary/30' : 'border-[#1a1a1a]',
                            cell.isWeekend && !cell.isToday && 'bg-white/[0.02]',
                            cell.isMonday && 'border-l border-[#27272A]',
                            cell.isFirstOfMonth && 'border-l-2 border-[#3a3a3a]'
                          )}
                          style={{ width: cell.width }}
                        />
                      ))}
                    </div>

                    {/* Today line */}
                    {cells.some((c) => c.isToday) && (
                      <div
                        className="absolute top-0 bottom-0 w-0.5 bg-primary z-20"
                        style={{
                          left: cells.findIndex((c) => c.isToday) * CELL_WIDTH[zoomLevel] + CELL_WIDTH[zoomLevel] / 2,
                        }}
                      />
                    )}

                    {/* Task bars */}
                    {tasks.map((task, index) => {
                      const position = calculateTaskPosition(task);
                      return (
                        <div
                          key={task.id}
                          className="absolute h-10 flex items-center"
                          style={{ top: index * 40 }}
                        >
                          <div
                            data-task-bar="true"
                            className="absolute h-7 rounded-md flex items-center px-2 cursor-pointer group shadow-md hover:brightness-110 transition-all"
                            style={{
                              left: position.left,
                              width: position.width,
                              backgroundColor: task.color,
                            }}
                            onMouseDown={(e) => handleMouseDown(e, task, 'drag')}
                            onDoubleClick={() => handleEditTask(task)}
                            data-testid={`gantt-bar-${task.id}`}
                          >
                            <div
                              className="absolute inset-y-0 left-0 rounded-l-md bg-black/20"
                              style={{ width: `${task.progress}%` }}
                            />
                            <span className="text-xs font-medium text-white truncate relative z-10 drop-shadow">
                              {task.name}
                            </span>
                            {task.notes && (
                              <FileText className="w-3 h-3 text-white/80 ml-1 relative z-10 flex-shrink-0" />
                            )}
                            <div
                              className="absolute right-0 top-0 bottom-0 w-3 cursor-ew-resize bg-white/30 rounded-r-md opacity-0 group-hover:opacity-100 transition-opacity"
                              onMouseDown={(e) => {
                                e.stopPropagation();
                                handleMouseDown(e, task, 'resize');
                              }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Task Dialog */}
        <Dialog key={orientationKey} open={showTaskDialog} onOpenChange={setShowTaskDialog}>
          <DialogContent className="bg-[#121214] border-[#27272A] w-[95vw] max-w-[600px] max-h-[90vh] overflow-y-auto" data-testid="task-dialog">
            <DialogHeader>
              <DialogTitle className="text-white">
                {editingTask ? 'Edit Task' : 'Create New Task'}
              </DialogTitle>
              <DialogDescription>
                {editingTask ? 'Update task details, notes, and attachments.' : 'Add a new task to your timeline.'}
              </DialogDescription>
            </DialogHeader>

            <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-2">
              <TabsList className="grid w-full grid-cols-3 bg-[#0A0A0B]">
                <TabsTrigger value="details" className="data-[state=active]:bg-primary">Details</TabsTrigger>
                <TabsTrigger value="notes" className="data-[state=active]:bg-primary">Notes</TabsTrigger>
                <TabsTrigger value="files" className="data-[state=active]:bg-primary">
                  Files {taskFiles.length > 0 && `(${taskFiles.length})`}
                </TabsTrigger>
              </TabsList>

              <TabsContent value="details" className="space-y-3 mt-3">
                <div className="space-y-1.5">
                  <Label htmlFor="task-name" className="text-xs">Task Name</Label>
                  <Input
                    id="task-name"
                    value={taskForm.name}
                    onChange={(e) => setTaskForm({ ...taskForm, name: e.target.value })}
                    placeholder="Enter task name"
                    className="bg-[#0A0A0B] border-[#27272A] h-9"
                    data-testid="task-name-input"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Start Date</Label>
                    {/* Mobile (portrait & landscape): native date input */}
                    <Input
                      type="date"
                      value={taskForm.start_date}
                      onChange={(e) => setTaskForm({ ...taskForm, start_date: e.target.value })}
                      className="lg:hidden bg-[#0A0A0B] border-[#27272A] h-9 text-sm"
                      data-testid="start-date-input-mobile"
                    />
                    {/* Desktop: calendar popover */}
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          type="button"
                          variant="outline"
                          className="hidden lg:flex w-full justify-start text-left font-normal bg-[#0A0A0B] border-[#27272A]"
                          data-testid="start-date-picker"
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {taskForm.start_date ? format(parseISO(taskForm.start_date), 'PP') : 'Pick date'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0 bg-[#121214] border-[#27272A]">
                        <Calendar
                          mode="single"
                          selected={taskForm.start_date ? parseISO(taskForm.start_date) : undefined}
                          onSelect={(date) =>
                            date && setTaskForm({ ...taskForm, start_date: format(date, 'yyyy-MM-dd') })
                          }
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-xs">End Date</Label>
                    {/* Mobile (portrait & landscape): native date input */}
                    <Input
                      type="date"
                      value={taskForm.end_date}
                      onChange={(e) => setTaskForm({ ...taskForm, end_date: e.target.value })}
                      className="lg:hidden bg-[#0A0A0B] border-[#27272A] h-9 text-sm"
                      data-testid="end-date-input-mobile"
                    />
                    {/* Desktop: calendar popover */}
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          type="button"
                          variant="outline"
                          className="hidden lg:flex w-full justify-start text-left font-normal bg-[#0A0A0B] border-[#27272A]"
                          data-testid="end-date-picker"
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {taskForm.end_date ? format(parseISO(taskForm.end_date), 'PP') : 'Pick date'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0 bg-[#121214] border-[#27272A]">
                        <Calendar
                          mode="single"
                          selected={taskForm.end_date ? parseISO(taskForm.end_date) : undefined}
                          onSelect={(date) =>
                            date && setTaskForm({ ...taskForm, end_date: format(date, 'yyyy-MM-dd') })
                          }
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Color</Label>
                    <div className="flex gap-1.5 items-center">
                      {TASK_COLORS.map((color) => (
                        <button
                          key={color.value}
                          type="button"
                          className={cn(
                            'w-6 h-6 rounded border-2 transition-all',
                            taskForm.color === color.value
                              ? 'border-white scale-110'
                              : 'border-transparent hover:scale-105'
                          )}
                          style={{ backgroundColor: color.value }}
                          onClick={() => setTaskForm({ ...taskForm, color: color.value })}
                        >
                          {taskForm.color === color.value && (
                            <Check className="w-3 h-3 text-white mx-auto" />
                          )}
                        </button>
                      ))}
                      {/* Custom color picker */}
                      <div className="relative">
                        <input
                          ref={colorInputRef}
                          type="color"
                          value={taskForm.color}
                          onChange={(e) => setTaskForm({ ...taskForm, color: e.target.value })}
                          className="absolute inset-0 w-6 h-6 opacity-0 cursor-pointer"
                          data-testid="custom-color-picker"
                        />
                        <button
                          type="button"
                          onClick={() => colorInputRef.current?.click()}
                          className={cn(
                            'w-6 h-6 rounded border-2 transition-all flex items-center justify-center cursor-pointer',
                            !TASK_COLORS.some(c => c.value === taskForm.color)
                              ? 'border-white scale-110'
                              : 'border-dashed border-muted-foreground hover:border-white hover:scale-105'
                          )}
                          style={{
                            backgroundColor: !TASK_COLORS.some(c => c.value === taskForm.color)
                              ? taskForm.color
                              : 'transparent'
                          }}
                        >
                          {!TASK_COLORS.some(c => c.value === taskForm.color) ? (
                            <Check className="w-3 h-3 text-white" />
                          ) : (
                            <Plus className="w-3 h-3 text-muted-foreground" />
                          )}
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-xs">Progress ({taskForm.progress}%)</Label>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={taskForm.progress}
                      onChange={(e) => setTaskForm({ ...taskForm, progress: parseInt(e.target.value) })}
                      className="w-full accent-primary h-2"
                    />
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="notes" className="mt-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Task Notes</Label>
                  <Textarea
                    value={taskForm.notes}
                    onChange={(e) => setTaskForm({ ...taskForm, notes: e.target.value })}
                    placeholder="Add notes..."
                    className="bg-[#0A0A0B] border-[#27272A] min-h-[120px] text-sm"
                    data-testid="task-notes-input"
                  />
                </div>
              </TabsContent>

              <TabsContent value="files" className="mt-3">
                {!editingTask ? (
                  <div className="text-center py-6 text-muted-foreground border border-dashed border-[#27272A] rounded-lg">
                    <Paperclip className="w-6 h-6 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">Save the task first to upload files</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <input
                        ref={fileInputRef}
                        type="file"
                        onChange={handleFileUpload}
                        className="hidden"
                        data-testid="file-upload-input"
                      />
                      <Button
                        variant="outline"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploadingFile}
                        className="border-[#27272A]"
                        data-testid="upload-file-btn"
                      >
                        {uploadingFile ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Upload className="w-4 h-4 mr-2" />
                        )}
                        Upload File
                      </Button>
                      <span className="text-xs text-muted-foreground">Max 25MB</span>
                    </div>

                    <div className="space-y-2 max-h-[250px] overflow-y-auto">
                      {taskFiles.length === 0 ? (
                        <div className="text-center py-6 text-muted-foreground border border-dashed border-[#27272A] rounded-lg">
                          <Paperclip className="w-6 h-6 mx-auto mb-2 opacity-50" />
                          <p className="text-sm">No files attached</p>
                        </div>
                      ) : (
                        taskFiles.map((file) => (
                          <div
                            key={file.id}
                            className="flex items-center gap-3 p-3 rounded-lg bg-[#0A0A0B] border border-[#27272A]"
                            data-testid={`file-item-${file.id}`}
                          >
                            <Paperclip className="w-4 h-4 text-primary flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-white truncate">{file.filename}</p>
                              <p className="text-xs text-muted-foreground">{formatFileSize(file.size)}</p>
                            </div>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDownloadFile(file.id, file.filename)}
                              className="h-8 w-8 text-muted-foreground hover:text-primary"
                              data-testid={`download-file-${file.id}`}
                            >
                              <Download className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDeleteFile(file.id)}
                              className="h-8 w-8 text-muted-foreground hover:text-destructive"
                              data-testid={`delete-file-${file.id}`}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </TabsContent>
            </Tabs>

            <DialogFooter className="mt-3 gap-2">
              <Button type="button" variant="outline" size="sm" onClick={() => setShowTaskDialog(false)} disabled={savingTask}>
                Cancel
              </Button>
              <Button type="button" size="sm" onClick={handleCreateTask} className="bg-primary" disabled={savingTask}>
                {savingTask ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {editingTask ? 'Updating...' : 'Creating...'}
                  </>
                ) : (
                  editingTask ? 'Update Task' : 'Create Task'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default GanttView;
